/**
 * The modulation matrix (docs/04): any parameter can be driven by any source, with an amount,
 * a response curve and attack/release smoothing.
 *
 *   effective = clamp(base + amount · curve(smooth(source)) · (max − min), min, max)
 *
 * Sources are normalized to 0..1. Targets are stable paths:
 *   layer/<layerId>/<paramId> · layer/<layerId>/opacity · fx/<fxId>/<paramId>
 *   palette/shift · master/intensity
 */

export type Curve = 'linear' | 'exp' | 'log' | 'scurve' | 'step' | 'invert';
export const CURVES: readonly Curve[] = ['linear', 'exp', 'log', 'scurve', 'step', 'invert'];

export interface Modulation {
  id: string;
  target: string;
  source: string;
  /** −1..1: how far (as a fraction of the parameter's range) the source can push it. */
  amount: number;
  curve: Curve;
  attackMs: number;
  releaseMs: number;
  /** Source 'osc': this modulation's own oscillator. */
  osc?: OscSpec;
  /** Swing both ways around the set value instead of only upwards. */
  bipolar?: boolean;
}

/** A modulation's own oscillator (source 'osc'), so each wobble can have its own speed. */
export interface OscSpec {
  shape: LfoShape;
  /** Cycles per second, or (when synced) beats per cycle. */
  rate: number;
  sync: boolean;
}

export const OSC_SOURCE = 'osc';

export type LfoShape = 'sine' | 'triangle' | 'saw' | 'square' | 'random';
export const LFO_SHAPES: readonly LfoShape[] = ['sine', 'triangle', 'saw', 'square', 'random'];

export interface Lfo {
  id: string;
  shape: LfoShape;
  /** Cycles per second, or (when synced) beats per cycle. */
  rate: number;
  sync: boolean;
}

export interface SourceDef {
  id: string;
  label: string;
  group: 'Audio' | 'Beat' | 'LFO' | 'Mood' | 'MIDI' | 'Other';
}

export const NUM_LFOS = 4;

/** Fixed sources, in the index order the renderer fills them. MIDI CC sources are appended at runtime. */
export const MOD_SOURCES: readonly SourceDef[] = [
  { id: 'audio.level', label: 'Level', group: 'Audio' },
  { id: 'audio.bass', label: 'Bass', group: 'Audio' },
  { id: 'audio.mid', label: 'Mids', group: 'Audio' },
  { id: 'audio.treble', label: 'Treble', group: 'Audio' },
  ...Array.from({ length: 8 }, (_, i) => ({
    id: `audio.band${i}`,
    label: `Band ${i + 1} (${['60', '120', '250', '500', '1k', '2k', '4k', '8k'][i]} Hz)`,
    group: 'Audio' as const,
  })),
  { id: 'audio.onset', label: 'Onset', group: 'Audio' },
  { id: 'audio.brightness', label: 'Brightness (centroid)', group: 'Audio' },
  { id: 'beat.pulse', label: 'Beat pulse', group: 'Beat' },
  { id: 'beat.phase', label: 'Beat ramp', group: 'Beat' },
  { id: 'beat.sine', label: 'Beat sine', group: 'Beat' },
  { id: 'beat.half', label: '½-beat ramp', group: 'Beat' },
  { id: 'beat.quarter', label: '¼-beat ramp', group: 'Beat' },
  { id: 'beat.bar', label: 'Bar ramp', group: 'Beat' },
  { id: 'beat.phrase', label: 'Phrase ramp', group: 'Beat' },
  { id: 'beat.anticipation', label: 'Anticipation', group: 'Beat' },
  { id: 'beat.random', label: 'Random per beat', group: 'Beat' },
  ...Array.from({ length: NUM_LFOS }, (_, i) => ({
    id: `lfo.${i + 1}`,
    label: `LFO ${i + 1}`,
    group: 'LFO' as const,
  })),
  { id: 'mood.energy', label: 'Energy', group: 'Mood' },
  { id: 'mood.arousal', label: 'Intensity (arousal)', group: 'Mood' },
  { id: 'mood.valence', label: 'Brightness of mood (valence)', group: 'Mood' },
  { id: 'mood.buildup', label: 'Build-up', group: 'Mood' },
  { id: 'mood.drop', label: 'Drop', group: 'Mood' },
  { id: 'time.slow', label: 'Slow drift', group: 'Other' },
  { id: 'melody.pitch', label: 'Melody pitch (low → high)', group: 'Audio' },
  { id: 'melody.note', label: 'Melody note (C → B)', group: 'Audio' },
  { id: 'melody.confidence', label: 'Melody clarity', group: 'Audio' },
  // Evaluated per modulation (Modulation.osc); its shared slot stays 0.
  { id: OSC_SOURCE, label: 'Own oscillator', group: 'LFO' },
];

export const SOURCE_INDEX: ReadonlyMap<string, number> = new Map(
  MOD_SOURCES.map((s, i) => [s.id, i]),
);

export function sourceLabel(id: string): string {
  const s = MOD_SOURCES.find((x) => x.id === id);
  if (s) return s.label;
  const cc = /^midi\.cc\.(\d+)\.(\d+)$/.exec(id);
  if (cc) return `MIDI CC ${cc[2]} (ch ${cc[1]})`;
  return id;
}

export function applyCurve(curve: Curve, x: number): number {
  const v = x < 0 ? 0 : x > 1 ? 1 : x;
  switch (curve) {
    case 'linear':
      return v;
    case 'exp':
      return v * v;
    case 'log':
      return Math.sqrt(v);
    case 'scurve':
      return v * v * (3 - 2 * v);
    case 'step':
      return Math.floor(v * 4 + 1e-9) / 4;
    case 'invert':
      return 1 - v;
  }
}

/** LFO value (0..1) at a phase in cycles. `rand` supplies a stable value per cycle for 'random'. */
export function lfoValue(shape: LfoShape, phase: number, rand: (cycle: number) => number): number {
  const f = phase - Math.floor(phase);
  switch (shape) {
    case 'sine':
      return 0.5 - 0.5 * Math.cos(f * 2 * Math.PI);
    case 'triangle':
      return f < 0.5 ? f * 2 : 2 - f * 2;
    case 'saw':
      return f;
    case 'square':
      return f < 0.5 ? 1 : 0;
    case 'random':
      return rand(Math.floor(phase));
  }
}

/** Phase in cycles of an oscillator at a given time (seconds) and beat position. */
export function oscPhase(o: OscSpec, time: number, beats: number): number {
  return o.sync ? beats / Math.max(0.01, o.rate) : time * o.rate;
}

/** Contribution (as a fraction of the range) of one modulation for a smoothed source value. */
export function contribution(
  amount: number,
  curve: Curve,
  value: number,
  bipolar: boolean,
): number {
  const c = applyCurve(curve, value);
  return amount * (bipolar ? c - 0.5 : c);
}

export function modulate(base: number, min: number, max: number, contributions: number): number {
  const v = base + contributions * (max - min);
  return v < min ? min : v > max ? max : v;
}

/** Deterministic hash → 0..1, used for "random per beat" and random LFOs. */
export function hash01(n: number): number {
  let x = Math.imul((n | 0) ^ 0x9e3779b9, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  x ^= x >>> 16;
  return (x >>> 0) / 4294967296;
}
